import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { certificationStatus } from "../../../constant";
import { DataTable } from "../../certifications/components/data-table";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const filterData = (data: any[], filter: any) => {
  let filteredData = [...data];
  if (filter?.search) {
    filteredData = filteredData.filter((item) => {
      // if manager dashboard then check userName too

      const searchTerm = filter.search.toLowerCase();
      const certName =
        item?.certification?.certificationName?.toLowerCase() ?? "";
      return certName.includes(searchTerm);
    });
  }
  if (filter.sortBy && filter.sortOrder) {
    filteredData = filteredData.sort((a, b) => {
      if (filter.sortBy === "createdAt") {
        // date filter
        const aDate = dayjs(a.createdAt).utc().format("YYYY-MM-DD");
        const bDate = dayjs(b.createdAt).utc().format("YYYY-MM-DD");
        const before =
          filter.sortOrder === "desc"
            ? dayjs(bDate).isBefore(aDate)
            : dayjs(aDate).isBefore(bDate);
        return before ? -1 : 1;
      }
      return filter.sortOrder === "desc"
        ? b[filter.sortBy] - a[filter.sortBy]
        : a[filter.sortBy] - b[filter.sortBy];
    });
  }
  return filteredData;
};

export function StatusCell({ status }: { status: string }) {
  const statusData =
    certificationStatus[status as keyof typeof certificationStatus] ||
    certificationStatus.NotStarted;
  return (
    <div
      className={`rounded-md w-[102px] h-[37px] flex items-center justify-center text-[13px]`}
      style={{
        backgroundColor: statusData?.bgColor,
        color: statusData?.textColor,
      }}
    >
      {statusData?.label}
    </div>
  );
}

export const getColumns = (
  data: any[],
  { onAction }: { onAction: (row: any) => void }
) => {
  const commonColumns = [
    {
      accessorKey: "firstName",
      header: ({ column }) => {
        return <div className="p-0">Name</div>;
      },
      cell: ({ row }) => {
        return (
          <div>
            {row.original.firstName} {row.original.lastName}
          </div>
        );
      },
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => {
        return <div>{row.original.email}</div>;
      },
    },
    {
      accessorKey: "manager",
      header: "Manager",
      cell: ({ row }) => {
        return <div>{row.original?.manager?.firstName}</div>;
      },
    },
    {
      accessorKey: "Date Created",
      header: ({ column }) => {
        return <div className="p-0">Date Created</div>;
      },
      cell: ({ row }) => {
        return (
          <span>
            {dayjs(row.original.createdAt).utc().format("MMM DD, YYYY")}
          </span>
        );
      },
    },

    {
      id: "actions",
      header: () => {
        return <div className="w-full flex justify-end">Session</div>;
      },
      cell: ({ row }) => {
        return (
          <div className="w-full flex justify-end">
            <button
              onClick={() => {
                onAction(row.original);
              }}
              className="text-sm text-center text-text2 hover:text-primary3"
            >
              Edit
            </button>
          </div>
        );
      },
    },
  ];
  return commonColumns;
};

export type User = {
  id: string;
  name: string;
  email: string;
  joinedOn: string;
  tier: string;
  totalSellers: number;
  status: string;
  statusText: string;
  statusDescription: string;
  totalSpend: string;
  stripeCustomerId: string;
  subscription: any;
  openDrawer: (row: any) => void;
  deleteModal: (row: any) => void;
  deactivateModal: (row: any) => void;
};

export function UserTable({
  filter,
  users,
  isLoading,
  refetch,
  onEditUser,
}: {
  filter?: any;
  users: any[];
  isLoading: boolean;
  refetch: () => void;
  onEditUser: (row: any) => void;
}) {
  const columns = useMemo<ColumnDef<User>[]>(
    () =>
      getColumns([], {
        onAction: (row) => onEditUser(row),
      }),
    [onEditUser]
  );

  const tableData = useMemo(
    () => filterData(users || [], filter || {}),
    [users, filter]
  );

  return (
    <div>
      <div className="flex flex-col gap-5 [&_.data-table-container]:!h-[calc(100dvh-300px)]">
        <DataTable
          columns={columns}
          data={tableData}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
